/**
 * Stripe Terminal SDK Integration
 * 
 * This utility provides a singleton instance of the Stripe Terminal SDK
 * and helper functions for reader discovery, connection, and payment processing.
 */

import { loadStripeTerminal } from '@stripe/terminal-js';
import { api } from '@/api/client';

let terminalInstance = null;
let currentReader = null;

/**
 * Initialize Stripe Terminal SDK
 * @returns {Promise<StripeTerminal>} Initialized Terminal instance
 */
export async function initializeTerminal() {
  if (terminalInstance) {
    return terminalInstance;
  }

  try {
    const StripeTerminal = await loadStripeTerminal();
    
    // Create terminal instance with connection token fetcher
    terminalInstance = StripeTerminal.create({
      onFetchConnectionToken: async () => {
        const { data } = await api.post('/stripe/connection-token');
        return data.secret;
      },
      onUnexpectedReaderDisconnect: () => {
        console.warn('[Terminal] Reader unexpectedly disconnected');
        currentReader = null;
      }
    });

    console.log('[Terminal] SDK initialized successfully');
    return terminalInstance;
  } catch (error) {
    console.error('[Terminal] Failed to initialize:', error);
    throw new Error(`Failed to initialize Stripe Terminal: ${error.message}`);
  }
}

/**
 * Discover available Stripe Terminal readers
 * @param {Object} options - Discovery options (simulated=true for test mode, location=stripe_location_id)
 * @returns {Promise<Array>} List of discovered readers
 */
export async function discoverReaders(options = {}) {
  const terminal = await initializeTerminal();
  
  // For real readers, location is REQUIRED by Stripe Terminal
  // Simulated readers don't need a location
  const config = {
    simulated: options.simulated || false
  };
  
  // Only add location if provided and not using simulated readers
  if (options.location && !options.simulated) {
    config.location = options.location;
  }

  console.log('[Terminal] Starting reader discovery with config:', config);

  const discoverResult = await terminal.discoverReaders(config);
  
  if (discoverResult.error) {
    console.error('[Terminal] Discovery error:', discoverResult.error);
    throw new Error(discoverResult.error.message);
  }

  console.log('[Terminal] Discovered readers:', discoverResult.discoveredReaders);
  return discoverResult.discoveredReaders;
}

/**
 * Connect to a specific reader
 * @param {Object} reader - Reader object from discovery
 * @returns {Promise<Object>} Connected reader
 */
export async function connectReader(reader) {
  const terminal = await initializeTerminal();
  
  console.log('[Terminal] Connecting to reader:', reader);
  console.log('[Terminal] Reader details:', {
    id: reader.id,
    label: reader.label,
    serial_number: reader.serial_number,
    device_type: reader.device_type,
    status: reader.status
  });
  
  try {
    const connectResult = await terminal.connectReader(reader);
    
    console.log('[Terminal] Connect result:', connectResult);
    
    if (connectResult.error) {
      console.error('[Terminal] Connection error:', connectResult.error);
      throw new Error(connectResult.error.message || 'Failed to connect to reader');
    }

    currentReader = connectResult.reader;
    console.log('[Terminal] Successfully connected to reader:', currentReader);
    
    // Save to localStorage for reference
    const readerInfo = {
      id: currentReader.id,
      label: currentReader.label,
      serial_number: currentReader.serial_number,
      device_type: currentReader.device_type,
      status: currentReader.status
    };
    
    console.log('[Terminal] Saving reader info to localStorage:', readerInfo);
    localStorage.setItem('stripe_terminal_reader', JSON.stringify(readerInfo));

    return currentReader;
  } catch (error) {
    console.error('[Terminal] Connection failed with exception:', error);
    throw new Error(`Failed to connect to reader: ${error.message}`);
  }
}

/**
 * Disconnect from current reader
 */
export async function disconnectReader() {
  const terminal = await initializeTerminal();
  
  if (!currentReader) {
    console.log('[Terminal] No reader to disconnect');
    return;
  }

  console.log('[Terminal] Disconnecting from reader');
  await terminal.disconnectReader();
  currentReader = null;
  localStorage.removeItem('stripe_terminal_reader');
}

/**
 * Get currently connected reader
 * @returns {Object|null} Current reader or null
 */
export function getCurrentReader() {
  return currentReader;
}

/**
 * Check if a reader is currently connected
 * @returns {boolean} True if reader is connected
 */
export function isReaderConnected() {
  return currentReader !== null;
}

/**
 * Ensure a reader is connected and ready for payment.
 * 
 * This solves the problem where localStorage has reader info (from a previous
 * connection in Settings) but the in-memory SDK state has been lost due to
 * a page navigation or refresh. It will:
 * 1. Check if the SDK already has an active connection → return immediately
 * 2. Check localStorage for a previously connected reader
 * 3. Re-discover readers and reconnect to the saved one
 * 
 * @returns {Promise<Object|null>} Connected reader or null if none available
 */
export async function ensureReaderConnected() {
  const terminal = await initializeTerminal();
  
  // Check if SDK already has an active connection
  const connectionStatus = await terminal.getConnectionStatus();
  if (connectionStatus === 'connected' && currentReader) {
    console.log('[Terminal] Already connected to reader:', currentReader.label);
    return currentReader;
  }

  // Check localStorage for a previously saved reader
  const savedReaderJson = localStorage.getItem('stripe_terminal_reader');
  if (!savedReaderJson) {
    console.log('[Terminal] No saved reader in localStorage');
    return null;
  }

  let savedReader;
  try {
    savedReader = JSON.parse(savedReaderJson);
  } catch (e) {
    console.warn('[Terminal] Invalid saved reader data, clearing');
    localStorage.removeItem('stripe_terminal_reader');
    return null;
  }

  console.log('[Terminal] Found saved reader, attempting reconnect:', savedReader.label || savedReader.serial_number);

  try {
    // Discover readers to find the saved one
    // Use location from saved reader if available, otherwise discover without
    const discoverResult = await terminal.discoverReaders({
      simulated: false
    });

    if (discoverResult.error) {
      console.error('[Terminal] Discovery error during reconnect:', discoverResult.error);
      return null;
    }

    const readers = discoverResult.discoveredReaders || [];
    console.log('[Terminal] Discovered', readers.length, 'reader(s) during reconnect');

    // Find the saved reader by serial number or ID
    const matchingReader = readers.find(r => 
      r.serial_number === savedReader.serial_number || r.id === savedReader.id
    );

    if (!matchingReader) {
      console.warn('[Terminal] Saved reader not found among discovered readers');
      // Clear stale localStorage since the reader is no longer available
      localStorage.removeItem('stripe_terminal_reader');
      return null;
    }

    // Reconnect to the matching reader
    console.log('[Terminal] Reconnecting to reader:', matchingReader.label || matchingReader.serial_number);
    const connectResult = await terminal.connectReader(matchingReader);

    if (connectResult.error) {
      console.error('[Terminal] Reconnection failed:', connectResult.error);
      localStorage.removeItem('stripe_terminal_reader');
      return null;
    }

    currentReader = connectResult.reader;
    console.log('[Terminal] Successfully reconnected to reader:', currentReader.label);

    // Update localStorage with fresh data
    localStorage.setItem('stripe_terminal_reader', JSON.stringify({
      id: currentReader.id,
      label: currentReader.label,
      serial_number: currentReader.serial_number,
      device_type: currentReader.device_type,
      status: currentReader.status
    }));

    return currentReader;
  } catch (error) {
    console.error('[Terminal] Reconnection error:', error);
    return null;
  }
}

/**
 * Check reader status and get battery level if available
 * @returns {Promise<Object>} Reader status information
 */
export async function getReaderStatus() {
  const terminal = await initializeTerminal();
  
  if (!currentReader) {
    return { connected: false };
  }

  // Get latest reader status
  const status = await terminal.getConnectionStatus();
  
  return {
    connected: status === 'connected',
    reader: currentReader,
    batteryLevel: currentReader.battery_level,
    status: status
  };
}

/**
 * Process a payment through the connected reader
 * @param {number} amount - Amount in dollars (e.g., 10.50)
 * @param {string} invoiceId - Invoice ID for reference
 * @returns {Promise<Object>} Payment result
 */
export async function processPayment(amount, invoiceId) {
  const terminal = await initializeTerminal();
  
  // If no reader in memory, try to auto-reconnect from localStorage
  if (!currentReader) {
    console.log('[Terminal] No reader in memory, attempting auto-reconnect...');
    await ensureReaderConnected();
  }
  
  if (!currentReader) {
    throw new Error('No reader connected. Please connect a reader first.');
  }

  console.log('[Terminal] Creating payment intent for $', amount);
  
  // Create payment intent on backend
  const { data } = await api.post('/stripe/terminal/payment-intent', {
    amount: amount,
    currency: 'usd',
    invoice_id: invoiceId
  });

  const clientSecret = data.client_secret;
  console.log('[Terminal] Payment intent created, collecting payment...');

  // Collect payment method using Terminal SDK
  const collectResult = await terminal.collectPaymentMethod(clientSecret);
  
  if (collectResult.error) {
    console.error('[Terminal] Payment collection error:', collectResult.error);
    throw new Error(collectResult.error.message);
  }

  console.log('[Terminal] Payment method collected, processing...');

  // Process the payment
  const processResult = await terminal.processPayment(collectResult.paymentIntent);
  
  if (processResult.error) {
    console.error('[Terminal] Payment processing error:', processResult.error);
    throw new Error(processResult.error.message);
  }

  console.log('[Terminal] Payment successful:', processResult.paymentIntent);

  return {
    success: true,
    paymentIntent: processResult.paymentIntent,
    paymentIntentId: processResult.paymentIntent.id,
    amount: processResult.paymentIntent.amount / 100, // Convert from cents to dollars
    cardBrand: processResult.paymentIntent.charges.data[0]?.payment_method_details?.card_present?.brand,
    cardLast4: processResult.paymentIntent.charges.data[0]?.payment_method_details?.card_present?.last4
  };
}

/**
 * Cancel an ongoing payment collection
 */
export async function cancelPayment() {
  const terminal = await initializeTerminal();
  
  console.log('[Terminal] Cancelling payment collection');
  await terminal.cancelCollectPaymentMethod();
}

export default {
  initializeTerminal,
  discoverReaders,
  connectReader,
  disconnectReader,
  getCurrentReader,
  isReaderConnected,
  ensureReaderConnected,
  getReaderStatus,
  processPayment,
  cancelPayment
};
